import type { Movie, MovieCategory } from "@/types/movie";
import { movieList } from "@/mocks/movies";
import { seriesList } from "@/mocks/series";
import { documentaryList } from "@/mocks/documentaries";
import { shortList, animationList } from "@/mocks/shorts";
import { categoryList } from "@/mocks/categories";

export const PAGE_SIZE = 20;

export const allMovies: Movie[] = [
  ...(movieList as Movie[]),
  ...(seriesList as Movie[]),
  ...(documentaryList as Movie[]),
  ...(shortList as Movie[]),
  ...(animationList as Movie[]),
];

export const categories: MovieCategory[] = categoryList as MovieCategory[];

export function getCategoryBySlug(slug?: string): MovieCategory | undefined {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategoryBySlug(slug)?.name ?? "全部影片";
}

export function getMovieById(id?: string): Movie | undefined {
  if (!id) return undefined;
  return allMovies.find((item) => item.id === id);
}

export function getMoviesByCategory(slug: string): Movie[] {
  return allMovies.filter((item) => item.category === slug);
}

export function countByCategory(slug: string): number {
  return allMovies.filter((item) => item.category === slug).length;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year}.${month}.${day}`;
}

export function getLatest(limit = 8): Movie[] {
  return [...allMovies]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit);
}

export function getHot(limit = 8): Movie[] {
  return [...allMovies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getHeroSlides(): Movie[] {
  const featuredIds = ["m-13", "m-02", "s-01", "m-07"];
  return featuredIds
    .map((id) => getMovieById(id))
    .filter((item): item is Movie => Boolean(item && item.backdrop));
}

export interface MovieFilter {
  region?: string;
  era?: string;
  genre?: string;
  sort?: string;
}

const matchesEra = (year: string, era?: string) => {
  if (!era || era === "全部年代") return true;
  if (era === "2020 及以前") return Number(year) <= 2020;
  return year === era;
};

export function filterMovies(list: Movie[], filter: MovieFilter): Movie[] {
  const { region, era, genre, sort } = filter;
  const filtered = list.filter((movie) => {
    if (region && region !== "全部地区" && movie.region !== region) return false;
    if (!matchesEra(movie.year, era)) return false;
    if (genre && genre !== "全部题材" && !movie.tags.includes(genre)) return false;
    return true;
  });

  switch (sort) {
    case "hot":
      return filtered.sort((a, b) => b.views - a.views);
    case "rating":
      return filtered.sort((a, b) => b.rating - a.rating);
    case "year":
      return filtered.sort((a, b) => Number(b.year) - Number(a.year));
    case "latest":
    default:
      return filtered.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return allMovies.filter((movie) => {
    const haystack = `${movie.title} ${movie.director} ${movie.tags.join(" ")} ${movie.summary}`.toLowerCase();
    return haystack.includes(key);
  });
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const sameCategory = allMovies.filter(
    (item) => item.id !== movie.id && item.category === movie.category,
  );
  const sameTag = allMovies.filter(
    (item) =>
      item.id !== movie.id &&
      item.category !== movie.category &&
      item.tags.some((tag) => movie.tags.includes(tag)),
  );
  return [...sameCategory, ...sameTag].slice(0, limit);
}

export function getRandomHot(limit = 4): Movie[] {
  return [...allMovies].sort((a, b) => b.views - a.views).slice(0, limit);
}